import {toJsDate} from '../../services/encounterUtils';
import {formatFacilityTime} from '../../utils/appointmentStatus';
import {parsePlannerTimeLabel} from './plannerModels';

const DATE_LOCALE = 'en-US';

const padDatePart = (value: any) => String(value).padStart(2, '0');

export function toPlannerDate(value: any) {
  return toJsDate(value);
}

function startOfLocalDay(value: any) {
  const date = toJsDate(value);
  return date
    ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
    : null;
}

export function isSameLocalDay(value: any, now = new Date()) {
  const day = startOfLocalDay(value);
  const today = startOfLocalDay(now);
  if (!day || !today) return !day;
  return day.getTime() === today.getTime();
}

export function localDateKey(value: any) {
  const date = toJsDate(value);
  if (!date) return '';
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join('-');
}

export function formatPlannerSidebarTime(value: any) {
  const date = toJsDate(value);
  if (!date) return '';
  return date.toLocaleTimeString(DATE_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatPlannerSidebarDateColumn(value: any, now = new Date()) {
  const date = toJsDate(value);
  if (!date) return '';

  const day = startOfLocalDay(date)!;
  const today = startOfLocalDay(now)!;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (day.getTime() === today.getTime()) return 'Today';
  if (day.getTime() === yesterday.getTime()) return 'Yesterday';
  if (day.getTime() === tomorrow.getTime()) return 'Tomorrow';

  return date.toLocaleDateString(DATE_LOCALE, {
    month: 'short',
    day: 'numeric',
  });
}

export function plannerDateGroupLabel(
  value: any,
  now = new Date(),
  {direction = 'past'}: Record<string, any> = {},
) {
  const date = toJsDate(value);
  if (!date) return 'No date';
  const day = startOfLocalDay(date)!;
  const today = startOfLocalDay(now)!;
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  if (day.getTime() === today.getTime()) return 'Today';
  if (direction === 'future' && day.getTime() === tomorrow.getTime())
    return 'Tomorrow';
  if (direction !== 'future' && day.getTime() === yesterday.getTime())
    return 'Yesterday';
  return date.toLocaleDateString(DATE_LOCALE, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function plannerEventDate(event: any, now = new Date()) {
  const date = toJsDate(event?.sortDate);
  if (date) return date;

  const parsedTime = parsePlannerTimeLabel(event?.time);
  if (!parsedTime) return null;
  const fallback = new Date(now);
  fallback.setHours(parsedTime.hour, parsedTime.minute, 0, 0);
  return fallback;
}

export function facilityDateString(
  instant = new Date(),
  timeZone = 'America/New_York',
) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

export function getSystemTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
}

export function formatPlannerFacilityTime(
  date: any,
  displayTimeZone: any,
  fallback = 'No time',
) {
  return date ? formatFacilityTime(date, displayTimeZone) : fallback;
}
